import { __extends } from "tslib";
import { NormalBasicMethod, _Shader_NormalBasicMethod } from './NormalBasicMethod';
/**
 * NormalSimpleWaterMethod provides a basic normal map method to create water ripples by translating two wave normal maps.
 */
var NormalSimpleWaterMethod = /** @class */ (function (_super) {
    __extends(NormalSimpleWaterMethod, _super);
    /**
     * Creates a new NormalSimpleWaterMethod object.
     * @param waveMap1 A normal map containing one layer of a wave structure.
     * @param waveMap2 A normal map containing a second layer of a wave structure.
     */
    function NormalSimpleWaterMethod(normalMap, secondaryNormalMap) {
        if (normalMap === void 0) { normalMap = null; }
        if (secondaryNormalMap === void 0) { secondaryNormalMap = null; }
        var _this = _super.call(this, normalMap) || this;
        _this._water1OffsetX = 0;
        _this._water1OffsetY = 0;
        _this._water2OffsetX = 0;
        _this._water2OffsetY = 0;
        _this._secondaryNormalMap = secondaryNormalMap;
        if (_this._secondaryNormalMap)
            _this.iAddTexture(_this._secondaryNormalMap);
        return _this;
    }
    Object.defineProperty(NormalSimpleWaterMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return NormalSimpleWaterMethod.assetType;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NormalSimpleWaterMethod.prototype, "water1OffsetX", {
        /**
         * The translation of the first wave layer along the X-axis.
         */
        get: function () {
            return this._water1OffsetX;
        },
        set: function (value) {
            this._water1OffsetX = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NormalSimpleWaterMethod.prototype, "water1OffsetY", {
        /**
         * The translation of the first wave layer along the Y-axis.
         */
        get: function () {
            return this._water1OffsetY;
        },
        set: function (value) {
            this._water1OffsetY = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NormalSimpleWaterMethod.prototype, "water2OffsetX", {
        /**
         * The translation of the second wave layer along the X-axis.
         */
        get: function () {
            return this._water2OffsetX;
        },
        set: function (value) {
            this._water2OffsetX = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NormalSimpleWaterMethod.prototype, "water2OffsetY", {
        /**
         * The translation of the second wave layer along the Y-axis.
         */
        get: function () {
            return this._water2OffsetY;
        },
        set: function (value) {
            this._water2OffsetY = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(NormalSimpleWaterMethod.prototype, "secondaryNormalMap", {
        /**
         * A second normal map that will be combined with the first to create a wave-like animation pattern.
         */
        get: function () {
            return this._secondaryNormalMap;
        },
        set: function (value) {
            if (this._secondaryNormalMap == value)
                return;
            if (this._secondaryNormalMap)
                this.iRemoveTexture(this._secondaryNormalMap);
            this._secondaryNormalMap = value;
            if (this._secondaryNormalMap)
                this.iAddTexture(this._secondaryNormalMap);
            this.invalidateShaderProgram();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @inheritDoc
     */
    NormalSimpleWaterMethod.prototype.dispose = function () {
        _super.prototype.dispose.call(this);
        this._secondaryNormalMap = null;
    };
    NormalSimpleWaterMethod.assetType = '[asset NormalSimpleWaterMethod]';
    return NormalSimpleWaterMethod;
}(NormalBasicMethod));
export { NormalSimpleWaterMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_NormalSimpleWaterMethod provides a basic normal map method to create water ripples by translating two wave normal maps.
 */
var _Shader_NormalSimpleWaterMethod = /** @class */ (function (_super) {
    __extends(_Shader_NormalSimpleWaterMethod, _super);
    /**
     * Creates a new NormalHeightMapChunk.
     */
    function _Shader_NormalSimpleWaterMethod(method, shader) {
        return _super.call(this, method, shader) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_NormalSimpleWaterMethod.prototype._initConstants = function () {
        _super.prototype._initConstants.call(this);
        var index = this._fragmentConstantsIndex;
        var data = this._shader.fragmentConstantData;
        data[index] = .5;
        data[index + 1] = 0;
        data[index + 2] = 0;
        data[index + 3] = 1;
    };
    /**
     * @inheritDoc
     */
    _Shader_NormalSimpleWaterMethod.prototype._initVO = function (chunkVO) {
        _super.prototype._initVO.call(this, chunkVO);
        var secondaryNormalMap = this._method.secondaryNormalMap;
        if (secondaryNormalMap) {
            this._secondaryNormalMap = secondaryNormalMap.getAbstraction(this._shader);
            this._shader.uvDependencies++;
        }
    };
    /**
     * @inheritDoc
     */
    _Shader_NormalSimpleWaterMethod.prototype.onClear = function (event) {
        _super.prototype.onClear.call(this, event);
        this._secondaryNormalMap = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_NormalSimpleWaterMethod.prototype._activate = function () {
        _super.prototype._activate.call(this);
        if (this._invalid) {
            var data = this._shader.fragmentConstantData;
            var index = this._fragmentConstantsIndex;
            data[index + 4] = this._method.water1OffsetX;
            data[index + 5] = this._method.water1OffsetY;
            data[index + 6] = this._method.water2OffsetX;
            data[index + 7] = this._method.water2OffsetY;
        }
        if (this._secondaryNormalMap)
            this._secondaryNormalMap.activate();
    };
    /**
     * @inheritDoc
     */
    _Shader_NormalSimpleWaterMethod.prototype._setRenderState = function (renderState) {
        _super.prototype._setRenderState.call(this, renderState);
        if (this._secondaryNormalMap)
            this._secondaryNormalMap._setRenderState(renderState);
    };
    /**
     * @inheritDoc
     */
    _Shader_NormalSimpleWaterMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code = '';
        var temp = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(temp, 1);
        var dataReg = registerCache.getFreeFragmentConstant();
        var dataReg2 = registerCache.getFreeFragmentConstant();
        this._fragmentConstantsIndex = dataReg.index * 4;
        code += 'add ' + temp + ', ' + sharedRegisters.uvVarying + ', ' + dataReg2 + '.xyxy\n';
        if (this._texture)
            code += this._texture._getFragmentCode(targetReg, registerCache, sharedRegisters, temp);
        code += 'add ' + temp + ', ' + sharedRegisters.uvVarying + ', ' + dataReg2 + '.zwzw\n';
        if (this._secondaryNormalMap)
            code += this._secondaryNormalMap._getFragmentCode(temp, registerCache, sharedRegisters, temp);
        code += 'add ' + targetReg + ', ' + targetReg + ', ' + temp + '		\n' +
            'mul ' + targetReg + ', ' + targetReg + ', ' + dataReg + '.x	\n' +
            'sub ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + sharedRegisters.commons + '.xxx	\n' +
            'nrm ' + targetReg + '.xyz, ' + targetReg + '.xyz							\n';
        return code;
    };
    return _Shader_NormalSimpleWaterMethod;
}(_Shader_NormalBasicMethod));
export { _Shader_NormalSimpleWaterMethod };
ShaderBase.registerAbstraction(_Shader_NormalSimpleWaterMethod, NormalSimpleWaterMethod);
